import React, { useState } from 'react';
import { MapPin, DollarSign, Shield, Zap, ChevronDown, ArrowRight, Star, Play, Smartphone, X, Bell } from 'lucide-react';
import emailjs from '@emailjs/browser';

const DARK = '#0D1117';
const WHITE = '#FFFFFF';
const TEAL = '#00A878';

const EMAILJS_SERVICE_ID = 'service_i8c9qzc';
const EMAILJS_NOTIFY_TEMPLATE_ID = 'template_dab2b76';
const EMAILJS_PUBLIC_KEY = 'fQM3oK6P6hlji2N7a';

// ── NOTIFY MODAL ──
function NotifyModal({ spot, onClose }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_NOTIFY_TEMPLATE_ID,
        {
          spot,
          email,
          phone: phone || 'Not provided',
          message: `User wants to be notified when ${spot} goes live on Spot Finder.`,
        },
        EMAILJS_PUBLIC_KEY
      );
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Get notified about ${spot}`}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      <div style={{
        background: WHITE, borderRadius: '20px',
        padding: '40px', width: '100%', maxWidth: '440px',
        boxShadow: '0 32px 64px rgba(0,0,0,0.2)',
        position: 'relative',
      }}>

        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close notification form"
          style={{
            position: 'absolute', top: '16px', right: '16px',
            background: '#f5f5f5', border: 'none', borderRadius: '8px',
            width: '32px', height: '32px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <X size={16} color={DARK} />
        </button>

        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              background: '#E8F5F1', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <Bell size={28} color={TEAL} />
            </div>
            <h3 style={{ fontWeight: '900', fontSize: '1.4rem', color: DARK, marginBottom: '12px' }}>
              You're on the list!
            </h3>
            <p style={{ color: '#6c757d', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '24px' }}>
              We'll notify you as soon as <strong>{spot}</strong> goes live on Spot Finder.
            </p>
            <button
              onClick={onClose}
              style={{
                background: TEAL, color: WHITE,
                border: 'none', borderRadius: '10px',
                padding: '12px 32px', fontWeight: '700',
                fontSize: '15px', cursor: 'pointer', width: '100%',
              }}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div style={{
              width: '56px', height: '56px', borderRadius: '14px',
              background: '#E8F5F1', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              marginBottom: '20px',
            }}>
              <Bell size={24} color={TEAL} />
            </div>

            <div style={{
              display: 'inline-block',
              background: '#E8F5F1', border: '1px solid #B2DFDB',
              borderRadius: '20px', padding: '4px 14px', marginBottom: '12px',
            }}>
              <span style={{ color: TEAL, fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px' }}>
                COMING SOON
              </span>
            </div>

            <h3 style={{ fontWeight: '900', fontSize: '1.4rem', color: DARK, marginBottom: '8px' }}>
              Notify me about<br />{spot}
            </h3>
            <p style={{ color: '#6c757d', fontSize: '0.88rem', lineHeight: 1.7, marginBottom: '24px' }}>
              Leave your details and we'll let you know the moment this location goes live.
            </p>

            {error && (
              <div style={{
                background: '#FEE2E2', border: '1px solid #FECACA',
                borderRadius: '10px', padding: '12px 16px',
                color: '#DC2626', fontSize: '13px', fontWeight: '600',
                marginBottom: '16px',
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: DARK, marginBottom: '8px', display: 'block' }}>
                  Email Address *
                </label>
                <input
                  type="email" required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="john@email.com"
                  style={{
                    width: '100%', padding: '12px 16px',
                    border: '1.5px solid #e9ecef', borderRadius: '10px',
                    fontSize: '14px', color: DARK, outline: 'none',
                    background: WHITE, transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = TEAL}
                  onBlur={e => e.target.style.borderColor = '#e9ecef'}
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: DARK, marginBottom: '8px', display: 'block' }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+254 700 000 000"
                  style={{
                    width: '100%', padding: '12px 16px',
                    border: '1.5px solid #e9ecef', borderRadius: '10px',
                    fontSize: '14px', color: DARK, outline: 'none',
                    background: WHITE, transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = TEAL}
                  onBlur={e => e.target.style.borderColor = '#e9ecef'}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? '#B2DFDB' : TEAL,
                  color: WHITE, border: 'none', borderRadius: '10px',
                  padding: '14px', fontWeight: '700', fontSize: '15px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  width: '100%', transition: 'background 0.2s',
                }}>
                {loading ? 'Sending...' : 'Notify Me'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState(null);
  const [notifySpot, setNotifySpot] = useState(null);

  const faqs = [
    { q: "Is Spot Finder free to use?", a: "Finding and viewing spots is completely free. Reserve in advance with a free account." },
    { q: "How accurate is the availability data?", a: "Spot data updates in real-time from verified parking operators across Nairobi." },
    { q: "Can I reserve a spot in advance?", a: "Yes — reserve up to 24 hours ahead, directly from the app or web." },
    { q: "Which areas does Spot Finder cover?", a: "CBD, Westlands, Kilimani, Upper Hill, and Parklands. More areas added monthly." },
    { q: "Can I access Spot Finder on multiple devices?", a: "Yes. Your account syncs across web, Android, and iOS instantly." },
  ];

  return (
    <div style={{ background: WHITE }}>

      {/* Notify Modal */}
      {notifySpot && (
        <NotifyModal
          spot={notifySpot}
          onClose={() => setNotifySpot(null)}
        />
      )}

      {/* ── HERO ── */}
      <div className="hero-outer" style={{ background: WHITE, padding: '0px 24px', paddingBottom: '60px' }}>
        <div className="hero-inner" style={{
          background: '#00A878',
          borderRadius: '16px',
          overflow: 'visible',
          position: 'relative',
          padding: '60px 125px 0',
          minHeight: '520px',
        }}>
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0, opacity: 0.06,
            backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
            backgroundSize: '24px 24px', borderRadius: '16px',
            pointerEvents: 'none',
          }} />
          <div aria-hidden="true" style={{
            position: 'absolute', top: '30px', right: '340px',
            color: 'rgba(255,255,255,0.35)', fontSize: '1.6rem',
            fontWeight: '900', fontStyle: 'italic',
            transform: 'rotate(-8deg)', pointerEvents: 'none',
          }}>wow!</div>

          <div className="row align-items-end" style={{ position: 'relative', zIndex: 1 }}>
            <div className="col-lg-6" style={{ paddingBottom: '60px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(0,0,0,0.15)', borderRadius: '20px',
                padding: '5px 14px', marginBottom: '24px',
              }}>
                <MapPin size={13} color={WHITE} aria-hidden="true" />
                <span style={{ color: WHITE, fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px' }}>
                  Discover Spots in Nairobi
                </span>
              </div>

              <h1 className="section-heading" style={{
                fontSize: '3.2rem', fontWeight: '900',
                lineHeight: 1.08, letterSpacing: '-1.5px', color: WHITE,
                marginBottom: '20px',
              }}>
                Find the perfect<br />parking spot
              </h1>

              <p style={{
                color: 'rgba(255,255,255,0.8)', fontSize: '1rem',
                margin: '0 0 32px', lineHeight: 1.75, maxWidth: '400px',
              }}>
                Stop circling the block. Spot Finder shows you real-time available bays so you drive straight in — every time.
              </p>

              <div className="d-flex gap-3 flex-wrap mb-5">
                <a href="https://play.google.com/store/apps/details?id=com.spotfinder.owner&hl=en"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on Google Play"
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: '1.5px solid rgba(255,255,255,0.4)',
                    borderRadius: '8px', padding: '11px 20px',
                    color: WHITE, fontWeight: '600', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    fontSize: '14px', textDecoration: 'none',
                  }}>
                  <Play size={14} fill={WHITE} color={WHITE} aria-hidden="true" /> Google Play
                </a>
                <a href="https://apps.apple.com/ke/app/spotfinder-parking-finder/id6572302355"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on App Store"
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: '1.5px solid rgba(255,255,255,0.4)',
                    borderRadius: '8px', padding: '11px 20px',
                    color: WHITE, fontWeight: '600', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    fontSize: '14px', textDecoration: 'none',
                  }}>
                  <Smartphone size={14} color={WHITE} aria-hidden="true" /> App Store
                </a>
                <a href="#spots" style={{
                  background: DARK, color: WHITE, borderRadius: '8px',
                  fontWeight: '700', padding: '11px 24px', fontSize: '14px',
                  textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  Find a Spot <ArrowRight size={16} aria-hidden="true" />
                </a>
              </div>

              <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '20px' }}>
                <div className="d-flex align-items-center gap-4 flex-wrap">
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: '600' }}>
                    More than 100+ companies trusted us
                  </span>
                  <div className="d-flex gap-3">
                    {['Microsoft', 'Evernote', 'Medium'].map(name => (
                      <span key={name} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: '700' }}>
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 d-flex justify-content-center align-items-end">
              <img
                className="hero-phone"
                src="/images/spotfinder-phone-mockup.webp"
                alt="Spot Finder mobile app showing parking availability in Nairobi"
                style={{
                  width: '380px', display: 'block',
                  marginBottom: '-80px', marginTop: '-80px',
                  filter: 'drop-shadow(0 32px 64px rgba(0,0,0,0.35))',
                  position: 'relative', zIndex: 2,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── WHY CHOOSE US ── */}
      <section aria-label="Why choose Spot Finder" style={{ background: '#F5F5F5', padding: '100px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <div style={{
              display: 'inline-block',
              background: '#E8F5F1', border: '1px solid #B2DFDB',
              borderRadius: '20px', padding: '6px 20px',
              color: TEAL, fontSize: '12px', fontWeight: '700',
              letterSpacing: '2px', marginBottom: '24px',
            }}>
              WHY CHOOSE US
            </div>
            <h2 className="section-heading" style={{
              fontWeight: '900', fontSize: '2.8rem',
              color: DARK, letterSpacing: '-1px', lineHeight: 1.15,
            }}>
              Dive into smarter parking<br />across diverse locations
            </h2>
          </div>
          <div className="row g-4">
            {[
              { icon: <MapPin size={26} color={WHITE} aria-hidden="true" />, bg: 'linear-gradient(135deg, #FF7043, #FF5722)', title: 'Real-Time Availability', desc: 'Live bay counts updated every 30 seconds. See exactly what is open before you leave your office or home.' },
              { icon: <Shield size={26} color={WHITE} aria-hidden="true" />, bg: 'linear-gradient(135deg, #26A69A, #00897B)', title: 'Safe & Verified Spots', desc: 'Every listed location is physically inspected. If it is on Spot Finder, it is open, safe, and operational.' },
              { icon: <DollarSign size={26} color={WHITE} aria-hidden="true" />, bg: 'linear-gradient(135deg, #FFA726, #FB8C00)', title: 'Transparent Pricing', desc: 'No hidden charges. Every spot shows the full hourly rate upfront so you can compare and choose freely.' },
              { icon: <Zap size={26} color={WHITE} aria-hidden="true" />, bg: 'linear-gradient(135deg, #26A69A, #00A878)', title: 'Instant Reservations', desc: 'Lock in your spot before you leave. Pay via M-Pesa in one tap and walk straight to your bay.' },
            ].map(({ icon, bg, title, desc }) => (
              <div className="col-md-6" key={title}>
                <article style={{
                  background: WHITE, borderRadius: '16px',
                  padding: '32px', display: 'flex',
                  alignItems: 'flex-start', gap: '24px',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
                  transition: 'box-shadow 0.2s, transform 0.2s', height: '100%',
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  <div style={{
                    background: bg, borderRadius: '14px',
                    width: '58px', height: '58px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }} aria-hidden="true">
                    {icon}
                  </div>
                  <div>
                    <h3 style={{ fontWeight: '800', color: DARK, marginBottom: '10px', fontSize: '1.05rem' }}>{title}</h3>
                    <p style={{ color: '#6c757d', fontSize: '0.88rem', margin: 0, lineHeight: 1.75 }}>{desc}</p>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APP PROMO ── */}
      <section aria-label="Spot Finder app features" style={{ background: '#F5F5F5', padding: '40px 24px' }}>
        <div className="app-promo-inner" style={{
          background: '#1B2A4A', borderRadius: '20px',
          overflow: 'hidden', padding: '60px',
        }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-6" style={{ position: 'relative' }}>
              <div style={{ borderRadius: '16px', height: '500px', overflow: 'hidden' }}>
                <img
                  src="/images/mockups.webp"
                  alt="Spot Finder app screens showing parking search, reservations and M-Pesa payment"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '16px' }}
                />
              </div>
              <div aria-label="1.2 million users downloaded" style={{
                position: 'absolute', bottom: '16px', left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '12px', padding: '14px 28px',
                display: 'flex', alignItems: 'center', gap: '10px',
                whiteSpace: 'nowrap',
              }}>
                <span style={{ color: WHITE, fontWeight: '900', fontSize: '1.4rem', letterSpacing: '-1px' }}>1.2M</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: '500' }}>User Downloaded</span>
              </div>
            </div>
            <div className="col-lg-6">
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '20px', padding: '6px 16px', marginBottom: '24px',
              }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px' }}>
                  ENHANCE YOUR PARKING
                </span>
              </div>
              <h2 className="section-heading" style={{
                fontWeight: '900', fontSize: '2.8rem',
                color: WHITE, lineHeight: 1.15, letterSpacing: '-1px', marginBottom: '0',
              }}>
                Park smarter with<br />the Spot Finder app
              </h2>
              <div aria-hidden="true" style={{ width: '100%', height: '2px', background: 'rgba(255,255,255,0.15)', margin: '24px 0' }} />
              <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, fontSize: '0.95rem', maxWidth: '440px', marginBottom: '32px' }}>
                There are many parking locations that you can choose from and have the opportunity to find the best spot accompanied by real-time directions and M-Pesa payments.
              </p>
              <div aria-hidden="true" style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.1)', marginBottom: '28px' }} />
              <div className="d-flex flex-wrap gap-2">
                {['CBD Parking', 'Westlands', 'Kilimani', 'Upper Hill', 'Parklands', 'Industrial Area', 'Gigiri', 'Karen'].map(tag => (
                  <span key={tag} style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    color: 'rgba(255,255,255,0.75)',
                    borderRadius: '8px', padding: '8px 16px',
                    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                    transition: 'background 0.2s, border-color 0.2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = TEAL; e.currentTarget.style.borderColor = TEAL; e.currentTarget.style.color = WHITE; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── POPULAR SPOTS ── */}
      <section id="spots" aria-label="Upcoming parking spots" style={{ background: '#F5F5F5', padding: '100px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <div style={{
              display: 'inline-block',
              background: '#E8F5F1', border: '1px solid #B2DFDB',
              borderRadius: '20px', padding: '6px 20px',
              color: TEAL, fontSize: '12px', fontWeight: '700',
              letterSpacing: '2px', marginBottom: '24px',
            }}>
              OUR BEST SPOTS
            </div>
            <h2 className="section-heading" style={{ fontWeight: '900', fontSize: '2.8rem', color: DARK, letterSpacing: '-1px' }}>
              Discover the most popular Parking Spots
            </h2>
            <p style={{ color: '#6c757d', fontSize: '0.95rem', maxWidth: '520px', margin: '16px auto 0' }}>
              We are onboarding verified parking operators across Nairobi. Coming soon to your area!
            </p>
          </div>

          <div className="row g-4 cards-row">
            {[
              { name: 'CBD Parking Plaza', area: 'Nairobi CBD', img: '/images/Nairobi CBD.webp' },
              { name: 'Westlands Secure Park', area: 'Westlands', img: '/images/Westlands.webp' },
              { name: 'Upper Hill Lot A', area: 'Upper Hill', img: '/images/Upperhill.webp' },
              { name: 'Kilimani Parking Hub', area: 'Kilimani', img: '/images/Kilimani.webp' },
              { name: 'Parklands Open Bay', area: 'Parklands', img: '/images/Parklands-Nairobi.webp' },
              { name: 'Industrial Area Yard', area: 'Industrial Area', img: '/images/industrial-area.webp' },
            ].map((spot, i) => (
              <div className="col-md-4" key={i}>
                <article style={{
                  background: WHITE, borderRadius: '16px', padding: '10px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0',
                  height: '100%', display: 'flex', flexDirection: 'column',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}>

                  <div style={{
                    borderRadius: '8px', overflow: 'hidden',
                    height: '180px', flexShrink: 0,
                    background: 'linear-gradient(135deg, #E8F5F1, #B2DFDB)',
                    position: 'relative',
                  }}>
                    <img
                      src={spot.img}
                      alt={`${spot.name} parking in ${spot.area}, Nairobi`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <div aria-hidden="true" style={{
                      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{
                        background: TEAL, color: WHITE,
                        borderRadius: '20px', padding: '6px 18px',
                        fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px',
                      }}>
                        COMING SOON
                      </span>
                    </div>
                  </div>

                  <div style={{ padding: '16px 16px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#8B949E', letterSpacing: '1.5px', marginBottom: '8px', display: 'block' }}>
                      {spot.area.toUpperCase()}
                    </span>
                    <h3 style={{ fontWeight: '800', color: DARK, fontSize: '1.1rem', lineHeight: 1.3, marginBottom: '8px' }}>
                      {spot.name}
                    </h3>
                    <div className="d-flex align-items-center gap-1 mb-3" style={{ color: '#8B949E', fontSize: '13px' }}>
                      <MapPin size={13} aria-hidden="true" />
                      <span>{spot.area}, Nairobi</span>
                    </div>
                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px', marginTop: 'auto' }}>
                      <button
                        onClick={() => setNotifySpot(spot.name)}
                        aria-label={`Get notified when ${spot.name} is available`}
                        style={{
                          background: '#F5F5F5', color: DARK,
                          border: '1.5px solid #e9ecef',
                          borderRadius: '8px', padding: '9px 20px',
                          fontWeight: '700', fontSize: '13px', cursor: 'pointer',
                          transition: 'background 0.2s, border-color 0.2s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = TEAL; e.currentTarget.style.color = WHITE; e.currentTarget.style.borderColor = TEAL; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#F5F5F5'; e.currentTarget.style.color = DARK; e.currentTarget.style.borderColor = '#e9ecef'; }}>
                        Notify Me
                      </button>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section aria-label="Spot Finder statistics" style={{ background: WHITE, padding: '100px 0' }}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div style={{
                display: 'inline-block',
                background: '#E8F5F1', border: '1px solid #B2DFDB',
                borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
              }}>
                <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
                  PARK SMARTER, NOT HARDER
                </span>
              </div>
              <h2 className="section-heading" style={{ fontWeight: '900', fontSize: '2.8rem', color: DARK, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: '20px' }}>
                Enjoyable Parking<br />Experiences Await You
              </h2>
              <p style={{ color: '#6c757d', fontSize: '0.95rem', lineHeight: 1.8, maxWidth: '480px' }}>
                You can find and reserve parking wherever and whenever you want easily. We try to make it easier for you to find the best spot every time.
              </p>
              <div aria-hidden="true" style={{ height: '1px', background: '#e9ecef', margin: '32px 0' }} />
              <div className="d-flex gap-5 flex-wrap">
                {[['10,000', 'Daily Active Users'], ['60%', 'Parking Time Saved']].map(([num, label]) => (
                  <div key={label}>
                    <div aria-label={`${num} ${label}`} style={{ fontSize: '2.8rem', fontWeight: '900', color: DARK, letterSpacing: '-2px', lineHeight: 1 }}>{num}</div>
                    <div style={{ color: '#8B949E', fontSize: '13px', marginTop: '6px' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-6" style={{ position: 'relative' }}>
              <div style={{ borderRadius: '16px', height: '480px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src="/images/Review-spotfinder.webp"
                  alt="Happy Spot Finder users at a parking lot in Nairobi"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
              <div aria-hidden="true" style={{
                position: 'absolute', top: '30px', right: '30px',
                color: 'rgba(0,0,0,0.15)', fontSize: '1.4rem',
                fontWeight: '900', fontStyle: 'italic',
                transform: 'rotate(10deg)', pointerEvents: 'none',
              }}>wow!</div>
              <div aria-label="Rated 4.9 out of 5 from 990k reviews" style={{
                position: 'absolute', bottom: '24px', right: '24px',
                background: WHITE, borderRadius: '14px', padding: '14px 22px',
                display: 'flex', alignItems: 'center', gap: '10px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              }}>
                <Star size={20} color="#F4C542" fill="#F4C542" aria-hidden="true" />
                <span style={{ fontWeight: '900', fontSize: '1.2rem', color: DARK }}>4.9</span>
                <span style={{ color: '#8B949E', fontSize: '13px', fontWeight: '500' }}>990k+ Reviews</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section aria-label="Frequently asked questions" style={{ background: '#F5F5F5', padding: '100px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <div style={{
              display: 'inline-block',
              background: '#E8F5F1', border: '1px solid #B2DFDB',
              borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
            }}>
              <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
                FREQUENTLY ASKED QUESTIONS
              </span>
            </div>
            <h2 className="section-heading" style={{ fontWeight: '900', fontSize: '2.8rem', color: DARK, letterSpacing: '-1px' }}>
              Got Questions? We've Got Answers!
            </h2>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {faqs.map((faq, i) => (
                <div key={i}
                  role="button"
                  tabIndex={0}
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  onKeyDown={e => e.key === 'Enter' && setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    background: WHITE, borderRadius: '14px',
                    padding: '24px 28px', marginBottom: '16px',
                    cursor: 'pointer', border: '1px solid #f0f0f0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'box-shadow 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h3 style={{ fontWeight: '700', margin: 0, fontSize: '1rem', color: DARK }}>{faq.q}</h3>
                    <div aria-hidden="true" style={{
                      flexShrink: 0, marginLeft: '16px',
                      transition: 'transform 0.2s',
                      transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}>
                      <ChevronDown size={20} color={DARK} />
                    </div>
                  </div>
                  {openFaq === i && (
                    <p style={{ color: '#6c757d', marginTop: '16px', marginBottom: 0, lineHeight: 1.8, fontSize: '0.92rem' }}>
                      {faq.a}
                    </p>
                  )}
                </div>
              ))}
              <div className="d-flex justify-content-center gap-3 mt-4 flex-wrap">
                <a href="/contact" style={{
                  background: WHITE, color: DARK,
                  border: '1.5px solid #e9ecef', borderRadius: '8px',
                  padding: '11px 28px', fontWeight: '700',
                  fontSize: '14px', textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center',
                }}>
                  See more FAQs
                </a>
                <a href="/contact" style={{
                  background: TEAL, color: WHITE,
                  border: 'none', borderRadius: '8px',
                  padding: '11px 28px', fontWeight: '700',
                  fontSize: '14px', textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center',
                }}>
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="cta-outer" style={{ background: '#F5F5F5', padding: '40px 24px 100px' }}>
        <div className="cta-inner" style={{
          background: TEAL, borderRadius: '20px',
          padding: '60px 60px 0', overflow: 'visible', position: 'relative',
        }}>
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0, opacity: 0.06,
            backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
            backgroundSize: '24px 24px', borderRadius: '20px', pointerEvents: 'none',
          }} />
          <div className="row align-items-end" style={{ position: 'relative', zIndex: 1 }}>
            <div className="col-lg-7" style={{ paddingBottom: '60px' }}>
              <h2 className="section-heading" style={{ fontWeight: '900', fontSize: '2.8rem', color: WHITE, lineHeight: 1.15, letterSpacing: '-1px', marginBottom: '16px' }}>
                Let's find your perfect<br />parking spot today!
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', marginBottom: '36px', lineHeight: 1.7, maxWidth: '480px' }}>
                Park smarter with real-time availability, M-Pesa payments, and instant reservations — anytime, anywhere.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <a href="https://play.google.com/store/apps/details?id=com.spotfinder.owner&hl=en"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on Google Play"
                  style={{
                    background: DARK, color: WHITE, border: 'none',
                    borderRadius: '10px', padding: '12px 22px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none',
                  }}>
                  <Play size={22} fill={WHITE} color={WHITE} aria-hidden="true" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>GET IT ON</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1.3 }}>Google Play</div>
                  </div>
                </a>
                <a href="https://apps.apple.com/ke/app/spotfinder-parking-finder/id6572302355"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on App Store"
                  style={{
                    background: DARK, color: WHITE, border: 'none',
                    borderRadius: '10px', padding: '12px 22px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none',
                  }}>
                  <Smartphone size={22} color={WHITE} aria-hidden="true" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>Download on the</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1.3 }}>App Store</div>
                  </div>
                </a>
              </div>
            </div>
            <div className="col-lg-5 d-flex justify-content-center align-items-end">
              <img
                className="cta-phone"
                src="/images/spotfinder-screen.webp"
                alt="Spot Finder app screen showing parking reservations"
                style={{
                  width: '480px', display: 'block',
                  marginBottom: '-80px', marginTop: '-40px',
                  filter: 'drop-shadow(0 32px 64px rgba(0,0,0,0.35))',
                  position: 'relative', zIndex: 2,
                }}
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}